/**
 * The Delivery tab: the two queues where a fact got stuck in transit.
 *
 * The outbox re-drive API has existed since the outbox was built and has never
 * had a screen, which meant the recovery for an abandoned money fact was someone
 * with database access. A row in that queue is money the portal owes another
 * system and has given up on — the least visible kind of problem there is, and
 * the one with the most consequences.
 *
 * Re-driving is a money action, not a retry button: it makes funds or books post
 * entries. So rows are named one at a time, a reason is required, and a step-up
 * code is required, exactly as for executing a payout.
 *
 * The bank half has no button, and that is the correct shape rather than missing
 * work. `fn_guard_bank_webhook_event` refuses every update to a closed row, and a
 * webhook is a NOTIFICATION that something happened at the bank, not the thing
 * itself; replaying one we already failed to apply re-applies stale facts. The
 * recovery is to read the account again from the bank, which the sync already does.
 */
import { useCallback, useEffect, useState } from 'react';
import StatusBadge from '../../components/StatusBadge';
import opsService, {
  type DeadLetteredEvent,
  type WebhookDeadLetter,
} from '../../services/ops.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import { approveMfaErrorMessage } from '../../lib/mfaApproval';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  MAX_REDRIVE_PER_CALL,
  byAgeOldestFirst,
  canRedrive,
  describeRedriveResult,
  isRedriveReady,
  webhookRecovery,
  whyCannotRedrive,
} from './ops.helpers';

const inputStyle = { border: `1px solid ${colors.border}` };

export default function OpsDeliveryTab({ canRedriveRows }: Readonly<{
  /** Re-driving is manager / super_admin. An agent sees the queue and cannot act. */
  canRedriveRows: boolean;
}>): JSX.Element {
  const [events, setEvents] = useState<DeadLetteredEvent[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookDeadLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dead, hooks] = await Promise.all([
        opsService.outboxDeadLetters(),
        opsService.webhookDeadLetters(),
      ]);
      setEvents(byAgeOldestFirst(dead));
      setWebhooks(byAgeOldestFirst(hooks));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the delivery queues.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  return (
    <div>
      {error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      {flash && (
        <div className="rounded-lg p-3 mb-4 text-sm"
             style={{ background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}>
          {flash}
        </div>
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      <OutboxQueue
        events={events}
        canAct={canRedriveRows}
        onDone={(msg) => { setFlash(msg); void reload(); }}
        onError={setError}
      />
      <WebhookQueue webhooks={webhooks} />
    </div>
  );
}

function OutboxQueue({ events, canAct, onDone, onError }: Readonly<{
  events: DeadLetteredEvent[];
  canAct: boolean;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [busy, setBusy] = useState(false);

  const toggle = (id: string): void => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async (): Promise<void> => {
    setBusy(true);
    try {
      const result = await opsService.redrive(selected, reason.trim(), mfaCode.trim());
      setSelected([]);
      setReason('');
      setMfaCode('');
      // Every row that did not go is named. A partial result reported as a clean
      // success is how an operator ends up selecting the same five rows twice.
      onDone(describeRedriveResult(result).text);
    } catch (e: unknown) {
      // The step-up codes have their own copy; anything else keeps a fallback that
      // names THIS action, not 'could not approve'.
      onError(
        getApiErrorCode(e)?.startsWith('mfa_') === true
          ? approveMfaErrorMessage(e)
          : getApiErrorMessage(e, 'Could not send these events again.'),
      );
    } finally {
      setBusy(false);
      setMfaCode('');
    }
  };

  const ready = isRedriveReady(selected, reason, mfaCode) && !busy;

  return (
    <section className="mb-8">
      <h2 className="text-sm font-bold uppercase tracking-widest mb-1"
          style={{ color: colors.inkSoft }}>
        Money facts we owe another system
      </h2>
      <p className="text-sm mb-3" style={{ color: colors.muted }}>
        Each row is something that happened here and never reached funds or books. Sending
        one again makes that system post entries, so it is recorded like any money action.
      </p>

      {events.length === 0 && (
        <div className="rounded-2xl p-6 text-sm"
             style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
          Nothing has been abandoned. Every event either landed or is still being retried.
        </div>
      )}

      <ul className="space-y-2">
        {events.map((e) => {
          const blocked = whyCannotRedrive(e);
          return (
            <li key={e.id} className="rounded-xl p-3"
                style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
              <div className="flex items-start gap-3">
                {canAct && canRedrive(e) && (
                  <input
                    type="checkbox"
                    aria-label={`Select ${e.event_type}`}
                    checked={selected.includes(e.id)}
                    onChange={() => toggle(e.id)}
                    disabled={busy}
                    className="mt-1"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm" style={{ color: colors.ink }}>
                    {e.event_type}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: colors.muted }}>
                    given up {formatDateTime(e.dead_lettered_at)} after {e.attempts} attempt(s)
                  </div>
                  {e.last_error && (
                    <div className="text-xs mt-1 font-mono" style={{ color: colors.mutedSoft }}>
                      {e.last_error}
                    </div>
                  )}
                  {blocked && (
                    <p className="text-xs mt-1" style={{ color: colors.warning }}>{blocked}</p>
                  )}
                </div>
                <StatusBadge tone={blocked ? 'neutral' : 'warning'}>
                  {blocked ? 'Refused' : 'Undelivered'}
                </StatusBadge>
              </div>
            </li>
          );
        })}
      </ul>

      {canAct && selected.length > 0 && (
        <div className="mt-4 rounded-2xl p-4"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
          <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
            {selected.length} of at most {MAX_REDRIVE_PER_CALL} selected. Say what was fixed —
            this is the record of why a money event was revived.
          </p>
          <label htmlFor="redrive-reason" className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            What was fixed?
          </label>
          <textarea
            id="redrive-reason"
            rows={2}
            value={reason}
            onChange={(ev) => setReason(ev.target.value.slice(0, 500))}
            placeholder="Funds refused these while its intake was on the old contract; it was redeployed at 14:20."
            disabled={busy}
            className="w-full px-3 py-2 rounded-lg text-sm mb-3"
            style={inputStyle}
          />
          <label htmlFor="redrive-mfa" className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            Authenticator code
          </label>
          <input
            id="redrive-mfa"
            value={mfaCode}
            onChange={(ev) => setMfaCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            disabled={busy}
            className="w-40 px-3 py-2 rounded-lg text-sm mb-3"
            style={inputStyle}
          />
          <div>
            <button type="button" disabled={!ready} onClick={() => { void submit(); }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                    style={{ background: colors.navy, color: '#FFFFFF' }}>
              {busy ? 'Sending…' : `Send ${selected.length} again`}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function WebhookQueue({ webhooks }: Readonly<{ webhooks: WebhookDeadLetter[] }>): JSX.Element {
  return (
    <section>
      <h2 className="text-sm font-bold uppercase tracking-widest mb-1"
          style={{ color: colors.inkSoft }}>
        Bank notifications we could not apply
      </h2>
      <p className="text-sm mb-3" style={{ color: colors.muted }}>
        These are not re-sent. A notification only reports what happened at the bank, so the
        fix is to read the account again rather than replay an old message.
      </p>

      {webhooks.length === 0 && (
        <div className="rounded-2xl p-6 text-sm"
             style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
          Every notification the bank sent has been applied.
        </div>
      )}

      <ul className="space-y-2">
        {webhooks.map((w) => (
          <li key={w.id} className="rounded-xl p-3"
              style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium text-sm" style={{ color: colors.ink }}>
                  {w.resource_type} · {w.operation_type}
                </div>
                <div className="text-xs mt-0.5" style={{ color: colors.muted }}>
                  received {formatDateTime(w.received_at)} · {w.attempts} attempt(s)
                  {w.last_error && ` · ${w.last_error}`}
                </div>
                <p className="text-xs mt-2" style={{ color: colors.inkSoft }}>
                  {webhookRecovery(w)}
                </p>
              </div>
              <StatusBadge tone="warning">Not applied</StatusBadge>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
